import { FileText } from 'lucide-react';
import { usePermissions } from '@/features/permissions';
import { useOrgDateFormat } from '@/lib/timezone';
import { useEncounterDocuments } from '../api/encounters-api';

/** Report summaries only: selecting the encounter opens its full report panel. */
export function EncounterReportList({ encounterId }: { encounterId: string }) {
  const { can } = usePermissions();
  const canRead = can('read_document');
  const query = useEncounterDocuments(canRead ? encounterId : undefined);
  const { formatInstant } = useOrgDateFormat();
  if (!canRead) return null;
  if (query.isLoading) return <p className="ml-6 mt-2 text-sm text-muted-foreground">Loading reports…</p>;
  if (query.isError)
    return (
      <p role="alert" className="ml-6 mt-2 text-sm">
        Reports could not be loaded. Open the encounter to retry.
      </p>
    );
  const reports = (query.data ?? [])
    .filter((report) => !report.deleted_at)
    .sort((a, b) => b.generated_at.localeCompare(a.generated_at) || a.id.localeCompare(b.id));
  if (!reports.length) return null;
  return (
    <ul aria-label="Encounter reports" className="ml-6 mt-2 flex list-none flex-col gap-2">
      {reports.map((report) => (
        <li key={report.id} className="flex items-start gap-2">
          <FileText aria-hidden="true" className="mt-1 size-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0">
            <span className="text-base font-medium">{report.title || report.template?.name || 'Generated report'}</span>
            <span className="text-sm text-muted-foreground">
              {' '}
              · Generated {formatInstant(report.generated_at, 'MMM d, yyyy')}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}
